function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { Fragment, useContext } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import Input from "./Input";
import { renderToStaticMarkup } from "react-dom/server";
import hash from "@emotion/hash";
import { ArrowDropDown } from "@churchofjesuschrist/eden-icons";
import { internalUseOnlyOpenDetailsStyles } from "./sharedCss";
import { spacing32 } from "@churchofjesuschrist/eden-style-constants";
import { ValidityStateContext } from "./validityState";
const ExtendedInput = styled(Input).withConfig({
  componentId: "sc-hcgesl-0"
})(["&::-webkit-calendar-picker-indicator{display:none !important;}", ";"], _ref => {
  let {
    displayAsSelect
  } = _ref;
  return displayAsSelect ? css(["", ";&::-webkit-list-button{position:absolute;opacity:0;top:0;right:0;width:", ";height:100%;pointer-events:all;}"], internalUseOnlyOpenDetailsStyles({
    Icon: ArrowDropDown,
    small: true
  }), spacing32) : `
      &::-webkit-list-button {
        opacity: 0;
        pointer-events: none;
      }`;
});

/**
 * As the user types into the TypeAhead component, the list of values predicts the users input based on a set list of data provided to the TypeAhead.
 */
const TypeAhead = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  let {
    validityState,
    small,
    name,
    pattern,
    required,
    minLength,
    maxLength,
    children,
    value,
    defaultValue,
    onChange,
    placeholder,
    displayAsSelect,
    autoComplete,
    id,
    ...props
  } = _ref2;
  const validityStateContext = useContext(ValidityStateContext);
  const childrenHash = hash(renderToStaticMarkup(children));
  const listId = `eden-type-ahead-${childrenHash}-${name}-${id}`;
  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(ExtendedInput, _extends({
    validityState: validityState ? validityState : validityStateContext,
    small,
    name,
    pattern,
    required,
    minLength,
    maxLength,
    value,
    defaultValue,
    onChange,
    placeholder,
    displayAsSelect,
    autoComplete,
    id
  }, props, {
    ref: ref,
    list: listId
  })), /*#__PURE__*/React.createElement("datalist", {
    id: listId
  }, children));
});
TypeAhead.displayName = "TypeAhead";
TypeAhead.propTypes = {
  /** The current validity state of the Input */
  validityState: PropTypes.oneOf(["error", "warning", "success", "unknown"]),
  /** A boolean indicating if the Input should be rendered in a smaller height */
  small: PropTypes.bool,
  /** Used to reference the input in JavaScript, or to reference form data after a form is submitted */
  name: PropTypes.string,
  /** Used as the unique `id` attribute of the container. This becomes required if there are two or more `TypeAhead` elements on the same page that have the same `name` and `children`. */
  id: PropTypes.string,
  /** A JavaScript regular expression ex:(^[A-Z].*) */
  pattern: PropTypes.string,
  /** Whether the input is required to be filled out to validate */
  required: PropTypes.bool,
  /** The number of characters must not be less than the minLength value */
  minLength: PropTypes.string,
  /** The number of characters must not exceed the maxLength value */
  maxLength: PropTypes.string,
  /** Content to render inside the TypeAhead Component. Children should be <option> elements. */
  children: PropTypes.node.isRequired,
  /** The value of the TypeAhead */
  value: PropTypes.string,
  /** Callback to execute when the value changes */
  onChange: PropTypes.func,
  /** Default value for uncontrolled use */
  defaultValue: PropTypes.string,
  /** Text that appears within the TypeAhead when empty */
  placeholder: PropTypes.string,
  /** Whether to look and behave like a select indicating dropdown menu options */
  displayAsSelect: PropTypes.bool,
  /** Specify what if any permission the user agent has to provide automated assistance [docs](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete) */
  autoComplete: PropTypes.string
};
export default TypeAhead;